import React, { useEffect } from "react";
import { Container, Row, Col } from "reactstrap";

const BRAND = "ZeroBuild Cabinets";
const COMPANY = "D2B Technologies Private Limited";

const productCategories = [
  { icon: "fa-solid fa-kitchen-set", label: "Kitchen Units" },
  { icon: "fa-solid fa-door-closed", label: "Wardrobes" },
  { icon: "fa-solid fa-tv", label: "TV Units" },
  { icon: "fa-solid fa-box-archive", label: "Storage Cabinets" },
];

const features = [
  {
    icon: "fa-solid fa-ruler-combined",
    title: "Multiple Size Options",
    desc: "Cabinets available across a wide range of standard dimensions to suit varied room layouts and design requirements.",
  },
  {
    icon: "fa-solid fa-layer-group",
    title: "Clean, Consistent Finishes",
    desc: "Uniform laminates and edge-banding applied under controlled conditions — no peeling, no inconsistencies.",
  },
  {
    icon: "fa-solid fa-medal",
    title: "Reliable Material Quality",
    desc: "Built with IS-grade plywood in MR and BWP variants. Every unit meets a defined material specification.",
  },
  {
    icon: "fa-solid fa-truck-fast",
    title: "Faster Project Execution",
    desc: "Ready-made units reduce on-site fabrication time, cut dependency on local material procurement, and simplify assembly.",
  },
  {
    icon: "fa-solid fa-screwdriver-wrench",
    title: "Hardware Included",
    desc: "Each cabinet ships with the necessary fittings. Hinges, channels, and shelf pins are pre-packed per unit.",
  },
  {
    icon: "fa-solid fa-file-lines",
    title: "Assembly Documentation",
    desc: "Every module includes labeled components and clear assembly drawings — suitable for any local carpenter.",
  },
];

export default function ZeroBuildPage() {
  useEffect(() => {
    document.title = `${BRAND} | Product line of ${COMPANY}`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        `${BRAND} is a branded line of ready-made modular cabinet carcasses by ${COMPANY}.`
      );
    } else {
      const tag = document.createElement("meta");
      tag.name = "description";
      tag.content = `${BRAND} is a branded line of ready-made modular cabinet carcasses by ${COMPANY}.`;
      document.head.appendChild(tag);
    }
  }, []);

  return (
    <div
      style={{
        fontFamily: "'Inter', sans-serif",
        color: "#1a1a2e",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Nav */}
      <header
        className="py-3 border-bottom"
        style={{ backgroundColor: "#ffffff" }}
      >
        <Container>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <img src="/store/zb_icon.png" alt="ZeroBuild Logo" height={32} />
              <div>
                <div className="fw-bold lh-1" style={{ color: "#E25822", fontSize: "1rem" }}>
                  {BRAND}
                </div>
                <div className="text-muted d-none d-md-block" style={{ fontSize: "0.7rem" }}>
                  by {COMPANY}
                </div>
              </div>
            </div>
            <span
              className="badge rounded-pill px-3 py-2"
              style={{
                backgroundColor: "#f4ede8",
                color: "#E25822",
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              B2B · MODULAR CABINETS
            </span>
          </div>
        </Container>
      </header>

      {/* Hero */}
      <section
        className="py-5"
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #283045 100%)",
          borderBottom: "3px solid #E25822",
        }}
      >
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg="8">
              <p
                className="mb-3 fw-semibold text-uppercase"
                style={{
                  color: "#E25822",
                  fontSize: "0.75rem",
                  letterSpacing: "2px",
                }}
              >
                A product line of {COMPANY}
              </p>
              <h1
                className="fw-bold mb-4"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  color: "#ffffff",
                  letterSpacing: "-1px",
                  lineHeight: 1.1,
                }}
              >
                {BRAND}
              </h1>
              <p
                className="mb-4"
                style={{
                  fontSize: "1.15rem",
                  color: "#c8cfd8",
                  maxWidth: 560,
                  margin: "0 auto 1.5rem",
                  lineHeight: 1.6,
                }}
              >
                Ready-made modular cabinet carcasses for kitchens, wardrobes,
                TV units, and storage.
              </p>
              <p
                className="mb-0"
                style={{
                  fontSize: "0.95rem",
                  color: "#8a94a6",
                  maxWidth: 520,
                  margin: "0 auto",
                  lineHeight: 1.7,
                }}
              >
                {BRAND} is a branded product line operated by {COMPANY},
                supplying factory-built cabinet carcasses directly to interior
                professionals and project sites across India.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Product Scope */}
      <section className="py-5" style={{ backgroundColor: "#f8f9fb" }}>
        <Container>
          <Row className="justify-content-center mb-4">
            <Col lg="7" className="text-center">
              <h2
                className="fw-bold mb-2"
                style={{ fontSize: "1.6rem", color: "#1a1a2e" }}
              >
                Product Scope
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
                {BRAND} supplies ready-made carcasses across the following
                cabinet categories.
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center gy-3">
            {productCategories.map((cat) => (
              <Col key={cat.label} xs="6" md="3">
                <div
                  className="text-center p-4 rounded-3 h-100"
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e4e8ef",
                  }}
                >
                  <i
                    className={`${cat.icon} mb-3`}
                    style={{ fontSize: "1.75rem", color: "#E25822" }}
                  />
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "0.95rem", color: "#1a1a2e" }}
                  >
                    {cat.label}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section className="py-5" style={{ backgroundColor: "#ffffff" }}>
        <Container>
          <Row className="justify-content-center mb-4">
            <Col lg="7" className="text-center">
              <h2
                className="fw-bold mb-2"
                style={{ fontSize: "1.6rem", color: "#1a1a2e" }}
              >
                What {BRAND} Delivers
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
                Designed to reduce complexity and improve consistency across
                interior projects.
              </p>
            </Col>
          </Row>
          <Row className="gy-4">
            {features.map((f) => (
              <Col key={f.title} xs="12" md="6" lg="4">
                <div className="d-flex gap-3 h-100">
                  <div
                    className="flex-shrink-0 d-flex align-items-start justify-content-center"
                    style={{ width: 36, paddingTop: 2 }}
                  >
                    <i
                      className={f.icon}
                      style={{ fontSize: "1.2rem", color: "#E25822" }}
                    />
                  </div>
                  <div>
                    <div
                      className="fw-semibold mb-1"
                      style={{ fontSize: "0.95rem", color: "#1a1a2e" }}
                    >
                      {f.title}
                    </div>
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: "0.875rem", lineHeight: 1.6 }}
                    >
                      {f.desc}
                    </p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* About */}
      <section className="py-5" style={{ backgroundColor: "#f8f9fb" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg="7">
              <h2
                className="fw-bold mb-4"
                style={{ fontSize: "1.6rem", color: "#1a1a2e" }}
              >
                About {BRAND}
              </h2>
              <p
                className="mb-3"
                style={{
                  fontSize: "0.975rem",
                  color: "#4a5568",
                  lineHeight: 1.75,
                }}
              >
                {BRAND} is a product line of{" "}
                <strong>{COMPANY}</strong>, a registered
                company operating in the interior and furniture supply space.
                The {BRAND} brand was created to address a specific gap in how
                cabinet carcasses are procured and supplied on project sites —
                moving from site-fabricated, inconsistent builds to
                factory-made, standardised units.
              </p>
              <p
                className="mb-3"
                style={{
                  fontSize: "0.975rem",
                  color: "#4a5568",
                  lineHeight: 1.75,
                }}
              >
                All {BRAND} products are manufactured to defined specifications
                and supplied directly to interior designers, architects,
                contractors, and project sites. {COMPANY}{" "}
                manages the full supply chain — from manufacturing to delivery.
              </p>
              <p
                className="mb-0"
                style={{
                  fontSize: "0.975rem",
                  color: "#4a5568",
                  lineHeight: 1.75,
                }}
              >
                {BRAND} is not an independent legal entity. It is a branded
                product offering owned and operated by{" "}
                <strong>{COMPANY}</strong>.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Legal / Contact Strip */}
      <section
        className="py-4 text-white"
        style={{
          backgroundColor: "#1a1a2e",
          borderTop: "3px solid #E25822",
        }}
      >
        <Container>
          <Row className="align-items-center gy-3">
            <Col md="6">
              <img src="/store/zb_icon.png" alt="ZeroBuild Logo" height={28} className="mb-1" />
              {BRAND}
              <div
                style={{ fontSize: "0.8rem" }}
              >
                A product line of {COMPANY}
              </div>
            </Col>
            <Col md="6" className="text-md-end">
              <div
                style={{ fontSize: "0.8rem" }}
              >
                M-10, 19th Avenue, Sector 10, Ashok Nagar, Chennai – 600083
              </div>
              <a
                href="mailto:hello@zerobuild.in"
                className="text-decoration-none"
                style={{ fontSize: "0.8rem", color: "#E25822" }}
              >
                hello@zerobuild.in
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer
        className="py-3"
        style={{ backgroundColor: "#12121f" }}
      >
        <Container>
          <p
            className="text-center mb-0"
            style={{ fontSize: "0.78rem", color: "#5a6478" }}
          >
            © {COMPANY}. All rights reserved. {BRAND} is a registered
            product line of {COMPANY}.
          </p>
        </Container>
      </footer>
    </div>
  );
}
